import { cookiesAccepted } from './cookies';

/**
 * Time in hours the persisted data will remain valid before it will be deleted
 * @type {number}
 */
const TIME_TO_EXPIRE_IN_HOURS = 48;

/**
 * Global key used in storage in which all data of this module is stored
 * @type {string}
 */
const PERSISTENCE_KEY = 'persistence_module_v1';

/**
 * Array of string which cannot be used when setting an item in storage
 * @type {string[]}
 */
const RESERVED_KEYS = ['updated_at'];

/**
 * Helper function to parse storage data from localStorage or sessionStorage.
 * Returns null if storage is empty or parsing fails.
 *
 * @param {Storage} storage - The storage object (localStorage or sessionStorage)
 * @returns {Object|null} Parsed data object or null
 */
function parseStorageData(storage) {
    const dataJSON = storage.getItem(PERSISTENCE_KEY);
    if (!dataJSON) {
        return null;
    }

    try {
        return JSON.parse(dataJSON);
    } catch (e) {
        return null;
    }
}

/**
 * Returns an item from session storage first, then falls back to local storage if not found.
 * @param {string} key The key of the item
 * @returns {any} The stored value or null if not found.
 */
export function getPersistentItem(key) {
    const sessionValue = getSessionItem(key);
    if (sessionValue !== null) {
        return sessionValue;
    }

    if (!cookiesAccepted('functional')) {
        return;
    }

    const storedData = parseStorageData(localStorage);
    if (!storedData) {
        return null;
    }

    if (!storedData[key]) {
        return null;
    }

    const storedDate = new Date(storedData.updated_at);
    const currentDate = new Date();

    const timeDifference = currentDate - storedDate;
    const timeDifferenceInHours = timeDifference / (1000 * 60 * 60);

    if (timeDifferenceInHours <= TIME_TO_EXPIRE_IN_HOURS) {
        return storedData[key];
    } else {
        clearAllPersistentItems();
        return null;
    }
}

/**
 * Parses the value to JSON and stores it in both session and local storage.
 * Session storage is always updated, local storage only if functional cookies are accepted.
 *
 * @param {string} key - The key for the item.
 * @param {any} value - The value to be stored.
 * @throws {Error} Will throw an error if key is a reserved key
 */
export function setPersistentItem(key, value) {
    if (RESERVED_KEYS.includes(key)) {
        throw new Error('Cannot set reserved key');
    }

    setSessionItem(key, value);

    if (cookiesAccepted('functional')) {
        const currentDate = new Date();
        const storedData = parseStorageData(localStorage) ?? {};

        storedData[key] = value;
        storedData['updated_at'] = currentDate.toISOString();

        localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(storedData));
    }
}

/**
 * Clear a specific key from both session and local storage
 *
 * @param {string} key - The key to be cleared.
 */
export function clearPersistentItem(key) {
    clearSessionItem(key);

    const storedData = parseStorageData(localStorage);
    if (!storedData) {
        return;
    }

    if (!storedData[key]) {
        return;
    }
    delete storedData[key];

    localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(storedData));
}

/**
 * Clears all persistent items from both session and local storage
 */
export function clearAllPersistentItems() {
    clearAllSessionItems();
    localStorage.removeItem(PERSISTENCE_KEY);
}

// Session storage helper functions
function getSessionItem(key) {
    const storedData = parseStorageData(sessionStorage);
    if (!storedData) {
        return null;
    }

    if (!storedData[key]) {
        return null;
    }

    return storedData[key];
}

function setSessionItem(key, value) {
    const storedData = parseStorageData(sessionStorage) ?? {};

    storedData[key] = value;

    sessionStorage.setItem(PERSISTENCE_KEY, JSON.stringify(storedData));
}

function clearSessionItem(key) {
    const storedData = parseStorageData(sessionStorage);
    if (!storedData) {
        return;
    }

    if (!storedData[key]) {
        return;
    }
    delete storedData[key];

    sessionStorage.setItem(PERSISTENCE_KEY, JSON.stringify(storedData));
}

function clearAllSessionItems() {
    sessionStorage.removeItem(PERSISTENCE_KEY);
}
